'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '../theme';
import type { RadioGroupProps, RadioItemProps } from './Radio.types';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, label, description, error, children, ...props }, ref) => {
  return (
    <div className="space-y-2">
      {label && (
        <label className="text-sm font-medium text-navy">{label}</label>
      )}
      {description && (
        <p className="text-sm text-warm-gray">{description}</p>
      )}
      <RadioGroupPrimitive.Root
        className={cn('grid gap-2', className)}
        {...props}
        ref={ref}
      >
        {children}
      </RadioGroupPrimitive.Root>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioItemProps
>(({ className, label, description, ...props }, ref) => {
  const id = React.useId();

  return (
    <div className="flex items-start space-x-3">
      <RadioGroupPrimitive.Item
        ref={ref}
        id={id}
        className={cn(
          'aspect-square h-4 w-4 rounded-full border border-warm-gray/40',
          'text-sage ring-offset-cream',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-sage data-[state=checked]:bg-sage',
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="h-2 w-2 fill-white text-white" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      <div className="space-y-1 leading-none">
        <label
          htmlFor={id}
          className="text-sm font-medium text-navy cursor-pointer"
        >
          {label}
        </label>
        {description && (
          <p className="text-sm text-warm-gray">{description}</p>
        )}
      </div>
    </div>
  );
});
RadioItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioItem };
